import { CircleDashed, MessageSquareText } from "lucide-react";

import usePlutoConversations from "../../hooks/usePlutoConversations";
import { relativeDay } from "./conversation-history-label";

type PlutoConversationHistoryListProps = {
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
};

export const PlutoConversationHistoryList = ({ activeConversationId, onSelect }: PlutoConversationHistoryListProps) => {
  const { data, isError, isPending } = usePlutoConversations(true);
  const conversations = data?.conversations ?? [];

  if (isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-stone-600">
        <CircleDashed aria-hidden="true" className="h-4 w-4 animate-spin" />
        Loading your conversations…
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-stone-600">Your conversation history is unavailable.</p>;
  }

  if (conversations.length === 0) {
    return (
      <div className="max-w-sm">
        <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 bg-white">
          <MessageSquareText aria-hidden="true" className="h-4 w-4 text-stone-700" />
        </span>
        <h2 className="text-base font-semibold text-stone-900">No conversations yet</h2>
        <p className="mt-2 text-sm leading-6 text-stone-600">
          Ask Pluto something and this is where you will find it again.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {conversations.map((conversation) => (
        <li key={conversation.id}>
          <button
            aria-current={conversation.id === activeConversationId ? "true" : undefined}
            className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors ${
              conversation.id === activeConversationId
                ? "border-stone-800 bg-white"
                : "border-stone-200 bg-white/70 hover:border-stone-400 hover:bg-white"
            }`}
            type="button"
            onClick={() => onSelect(conversation.id)}
          >
            <p className="truncate text-sm font-medium text-stone-900">{conversation.title}</p>
            <p className="mt-0.5 text-[11px] text-stone-500">{relativeDay(conversation.updatedAt)}</p>
          </button>
        </li>
      ))}
    </ul>
  );
};
